import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Popover } from '@mui/material';

import { ReactComponent as CashWalletInfoIcon } from '@/img/icons/cash-wallet-info-icon.svg';

// The paper is portaled to <body> and sits a few pixels below the icon, so leaving the icon must not
// close the popover instantly — this grace period lets the pointer cross the gap into the popover body.
const CLOSE_DELAY_MS = 120;

const InfoPopover = ({ title, children }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const closeTimerRef = useRef(null);
    const open = Boolean(anchorEl);

    const cancelScheduledClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const handleOpen = (event) => {
        cancelScheduledClose();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        cancelScheduledClose();
        setAnchorEl(null);
    };

    const scheduleClose = () => {
        cancelScheduledClose();
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setAnchorEl(null);
        }, CLOSE_DELAY_MS);
    };

    useEffect(() => cancelScheduledClose, []);

    return (
        <span className="cash-wallet-helper-info" onMouseLeave={scheduleClose} onBlur={handleClose}>
            <IconButton size="small" aria-label={title} onMouseEnter={handleOpen} onFocus={handleOpen}>
                <CashWalletInfoIcon className="cash-wallet-helper-info-icon" />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                // Hover-driven: the invisible modal backdrop must not swallow the pointer (it would fire an
                // immediate mouse-leave on the anchor), and focus must stay on the icon so onBlur/Escape
                // keep behaving. `pointer-events` is inherited, so the paper opts itself back in.
                sx={{ pointerEvents: 'none' }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                disableScrollLock
                PaperProps={{
                    className: 'cash-wallet-info-popover-paper',
                    sx: { pointerEvents: 'auto' },
                    onMouseEnter: cancelScheduledClose,
                    onMouseLeave: handleClose,
                }}
            >
                <svg
                    className="cash-wallet-info-popover-caret"
                    width="10"
                    height="4"
                    viewBox="0 0 10 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M5.83333 3.6648L10 0H0L4.16667 3.6648C4.6269 4.11174 5.3731 4.11174 5.83333 3.6648Z"
                        fill="white"
                    />
                </svg>
                <div className="cash-wallet-info-popover">
                    {title && <div className="cash-wallet-info-popover-title">{title}</div>}
                    <div className="cash-wallet-info-popover-body">{children}</div>
                </div>
            </Popover>
        </span>
    );
};

export default InfoPopover;
